// `AvatarUploader` — drag-and-drop avatar pipeline (US-182).
//
// Wires the W3 R2 presigned-URL flow:
//   1. POST `/me/avatar/sign-upload` → returns `{ uploadUrl, key, maxBytes }`.
//   2. PUT the file directly to R2 via `uploadUrl`.
//   3. POST `/me/avatar/complete` with `{ key }` → server validates magic
//      bytes + size, persists `users.image`, returns the public URL.
//
// We surface a 56px circle (proto line 645) showing the user's initials
// or current image, plus a "Changer l'avatar" CTA + "Retirer" delete.
// The dropzone behind the circle accepts drag-and-drop too. State machine:
//
//   idle → presigning → uploading → completing → idle (success)
//                                              ↘ error (toast + reset)
//
// Cropping (recon Open Q 15) is intentionally deferred — the API enforces
// a 2 MB cap and accepts only JPG/PNG/GIF, so a square crop is not
// strictly necessary for v1. We pass `crossOrigin="anonymous"` so the
// public R2 URL can be cached without CORS surprises.

import { useRef, useState, type ChangeEvent, type DragEvent } from 'react';

import { useCompleteAvatar, useDeleteAvatar, useSignAvatarUpload } from '@barel/api-client/react';
import type { AuthUser } from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import { Avatar, Button, Spinner, toast } from '@barel/ui';
import { Trash2, Upload } from 'lucide-react';

import { deriveInitials } from '../lib/initials';

const ALLOWED_TYPES = new Set<string>(['image/jpeg', 'image/png', 'image/gif']);
const MAX_BYTES = 2 * 1024 * 1024; // mirrors the server cap (storage.md)

export interface AvatarUploaderProps {
  user: AuthUser | undefined;
}

export type UploaderState = 'idle' | 'presigning' | 'uploading' | 'completing' | 'error';

export function AvatarUploader({ user }: AvatarUploaderProps) {
  const t = useT('settings');
  const sign = useSignAvatarUpload();
  const complete = useCompleteAvatar();
  const remove = useDeleteAvatar();
  const [state, setState] = useState<UploaderState>('idle');
  const [dragOver, setDragOver] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const initials = deriveInitials(user?.name, user?.email);

  const handleFile = async (file: File): Promise<void> => {
    if (!ALLOWED_TYPES.has(file.type)) {
      toast.error({ title: t('profile.avatarBadType') });
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error({ title: t('profile.avatarTooLarge') });
      return;
    }
    setState('presigning');
    try {
      const presigned = await sign.mutateAsync({ contentType: file.type });
      setState('uploading');
      const putResp = await fetch(presigned.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!putResp.ok) throw new Error('R2 PUT failed');
      setState('completing');
      await complete.mutateAsync({ key: presigned.key });
      setState('idle');
      toast.success({ title: t('profile.avatarSuccess') });
    } catch {
      setState('error');
      toast.error({ title: t('profile.avatarError') });
      // Reset to idle after a short tick so the user can retry.
      setTimeout(() => setState('idle'), 50);
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (!file) return;
    void handleFile(file);
    // Reset value so picking the same file twice still triggers `change`.
    event.target.value = '';
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setDragOver(false);
    const file = event.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  };

  const handleRemove = async (): Promise<void> => {
    try {
      await remove.mutateAsync();
      toast.success({ title: t('profile.avatarSuccess') });
    } catch {
      toast.error({ title: t('profile.avatarError') });
    }
  };

  const isBusy = state !== 'idle';
  const showSpinner = state === 'presigning' || state === 'uploading' || state === 'completing';

  return (
    <div
      data-component="AvatarUploader"
      data-state={state}
      data-drag-over={dragOver || undefined}
      onDragOver={(event) => {
        event.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      className="flex items-center gap-4 rounded-lg border border-default bg-bg-surface p-4 data-[drag-over=true]:border-accent-border"
    >
      <button
        type="button"
        aria-label={t('profile.avatarChange')}
        onClick={() => inputRef.current?.click()}
        disabled={isBusy}
        className="relative inline-flex shrink-0 cursor-pointer items-center justify-center"
        data-testid="settings-avatar-trigger"
      >
        <Avatar
          size={56}
          src={user?.image ?? undefined}
          initials={initials}
          tone="identity"
          identityColor="var(--bg-elevated)"
          alt={t('profile.avatarLabel')}
        />
        {showSpinner ? (
          <span className="absolute inset-0 flex items-center justify-center rounded-full bg-bg-surface/70">
            <Spinner size="sm" />
          </span>
        ) : null}
      </button>

      <div className="flex flex-1 flex-col gap-1">
        <span className="text-sm font-medium text-primary">{t('profile.avatarLabel')}</span>
        <span className="text-xs text-tertiary">{t('profile.avatarHint')}</span>
        <span className="text-xs text-tertiary">{t('profile.avatarDropHint')}</span>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="secondary"
          iconLeft={<Upload size={14} />}
          onClick={() => inputRef.current?.click()}
          disabled={isBusy}
          data-testid="settings-avatar-pick"
        >
          {t('profile.avatarChange')}
        </Button>
        {user?.image ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            iconLeft={<Trash2 size={14} />}
            onClick={() => {
              void handleRemove();
            }}
            disabled={isBusy || remove.isPending}
            data-testid="settings-avatar-remove"
          >
            {t('profile.avatarRemove')}
          </Button>
        ) : null}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif"
        className="sr-only"
        onChange={handleInputChange}
        data-testid="settings-avatar-input"
      />
    </div>
  );
}
